import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import { Store, Building2, Mail, Lock, Loader2, AlertCircle, Users, Eye, EyeOff } from "lucide-react";
import { loginReseller, loginMerchant, loginStaff } from "@/shared/api/auth";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function PortalLogin() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("merchant");
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [isStaffLogin, setIsStaffLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ mode: "onTouched" });

  const handleTabChange = (value) => {
    setActiveTab(value);
    setServerError("");
    reset();
  };

  const onSubmit = async (data) => {
    setServerError("");
    setLoading(true);

    try {
      if (isStaffLogin) {
        const panelTypeMap = {
          reseller: "RESELLER",
          merchant: "MERCHANT",
        };
        await loginStaff(data.email, data.password, panelTypeMap[activeTab]);
      } else {
        switch (activeTab) {
          case "reseller":
            await loginReseller(data.email, data.password);
            break;
          case "merchant":
            await loginMerchant(data.email, data.password);
            break;
        }
      }

      const dashboardMap = {
        reseller: "/ResellerDashboard",
        merchant: "/MerchantDashboard",
      };

      navigate(dashboardMap[activeTab]);
    } catch (err) {
      setServerError(err.message || t("loginFailed"));
      setLoading(false);
    }
  };

  const tabConfig = [
    {
      value: "merchant",
      label: t("merchants"),
      icon: Building2,
      color: "from-amber-500 to-orange-600",
      bgColor: "amber",
      description: t("merchantBusinessAccess"),
    },
    {
      value: "reseller",
      label: t("resellers"),
      icon: Store,
      color: "from-emerald-500 to-teal-600",
      bgColor: "emerald",
      description: t("resellerPartnerAccess"),
    },
  ];

  const currentTab = tabConfig.find((t) => t.value === activeTab);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <style>{`
        .login-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .glow-effect-violet { box-shadow: 0 0 60px rgba(139, 92, 246, 0.3); }
        .glow-effect-emerald { box-shadow: 0 0 60px rgba(16, 185, 129, 0.3); }
        .glow-effect-amber { box-shadow: 0 0 60px rgba(245, 158, 11, 0.3); }
      `}</style>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <motion.div
            key={activeTab}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${currentTab?.color} shadow-lg mb-4 glow-effect-${currentTab?.bgColor}`}
          >
            {currentTab && <currentTab.icon className="h-10 w-10 text-white" />}
          </motion.div>
          <h1 className="text-3xl font-bold text-white mb-2">Luxkgate Portal</h1>
          <p className="text-slate-400">{currentTab?.description}</p>
        </div>

        {/* Login Card */}
        <Card className="login-card border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl text-white">{t("signInToPortal")}</CardTitle>
            <CardDescription className="text-slate-400">
              {t("selectAccountTypeAndEnterCredentials")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Account Type Tabs */}
            <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-6">
              <TabsList className="grid grid-cols-2 bg-slate-800/50 p-1">
                {tabConfig.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-slate-700 data-[state=active]:to-slate-600 data-[state=active]:text-white text-slate-400"
                  >
                    <tab.icon className="h-4 w-4 mr-1.5" />
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              {/* Server Error */}
              {serverError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {serverError}
                </motion.div>
              )}

              {/* Email Field */}
              <div className="space-y-1">
                <Label className="text-slate-300">{t("emailAddress")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type="email"
                    placeholder={`${activeTab}@luxkgate.com`}
                    className={`pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-slate-500 focus:ring-slate-500/20 ${
                      errors.email ? "border-red-500/60 focus:border-red-500" : ""
                    }`}
                    {...register("email", {
                      required: "Email address is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Please enter a valid email address",
                      },
                    })}
                  />
                </div>
                {errors.email && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-1 text-xs text-red-400 mt-1"
                  >
                    <AlertCircle className="h-3 w-3 flex-shrink-0" />
                    {errors.email.message}
                  </motion.p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-1">
                <Label className="text-slate-300">{t("password")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className={`pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-slate-500 focus:ring-slate-500/20 ${
                      errors.password ? "border-red-500/60 focus:border-red-500" : ""
                    }`}
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 8,
                        message: "Password must be at least 8 characters",
                      },
                      validate: {
                        hasUppercase: (value) => /[A-Z]/.test(value) || "Password must contain at least one uppercase letter",
                        hasLowercase: (value) => /[a-z]/.test(value) || "Password must contain at least one lowercase letter",
                      }
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-1 text-xs text-red-400 mt-1"
                  >
                    <AlertCircle className="h-3 w-3 flex-shrink-0" />
                    {errors.password.message}
                  </motion.p>
                )}
              </div>

              {/* Staff Login Toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="staffLogin"
                  checked={isStaffLogin}
                  onChange={(e) => setIsStaffLogin(e.target.checked)}
                  className="rounded border-slate-600 bg-slate-800 text-violet-500 focus:ring-violet-500/20"
                />
                <label htmlFor="staffLogin" className="flex items-center gap-2 text-slate-400 cursor-pointer text-sm">
                  <Users className="h-4 w-4" />
                  {t("iAmStaffMember")}
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className={`w-full bg-gradient-to-r ${currentTab?.color} hover:opacity-90 text-white font-medium py-5 rounded-xl transition-all duration-200 shadow-lg`}
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t("signInAs").replace("{type}", `${currentTab?.label}${isStaffLogin ? ` ${t("staff")}` : ""}`)
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-700/50 text-center">
              <p className="text-slate-500 text-sm">
                {t("needHelpAccessingAccount")}
              </p>
              <a href="#" className="text-violet-400 hover:text-violet-300 text-xs transition-colors">
                {t("contactSupport")}
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-xs">
            © {new Date().getFullYear()} Luxkgate. {t("allRightsReserved")}
          </p>
        </div>
      </motion.div>
    </div>
  );
}
